import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Siren, FlaskConical, BookOpen, ClipboardList, ScrollText, Plus, Trash2,
  Search, Copy, X, ArrowRight, Lock,
} from "lucide-react";
import { RED_FLAGS, PANELS, CONDITIONS, SYSTEM_HUE, APPOINTMENT_TOPICS } from "@/lib/clinicData";

const HUE = "#E26D5A";
const DEEP = "#8E2E1D";

function Head({ num, title, line, chip, dark }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: dark ? "rgba(255,255,255,0.4)" : "#A67C70" }}>{num}</p>
        <h2 className="font-display text-3xl sm:text-4xl mt-1.5 leading-none" style={{ color: dark ? "#fff" : "#3A1810" }}>{title}</h2>
        <p className="text-sm mt-2 max-w-md" style={{ color: dark ? "rgba(255,255,255,0.55)" : "#8A6157" }}>{line}</p>
      </div>
      {chip && (
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-wide" style={dark ? { background: "rgba(255,255,255,0.1)", color: "#F5C4B6" } : { background: "rgba(226,109,90,0.14)", color: DEEP }}>
          <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: HUE }} />{chip}
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- 01 signal board */
function Flags() {
  const [open, setOpen] = useState(null);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(160deg, #2A0C08, #4A1710 60%, #2A0C08)" }} data-testid="deck-flags">
      <div className="absolute -top-24 right-[10%] w-[36vw] h-[36vw] rounded-full blur-[120px] opacity-30 drift" style={{ background: "radial-gradient(circle, #FF5A3C, transparent 70%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grain">
        <Head num="01 · the board" title="Never wait" line="Twelve signals. If one of them is yours, stop reading and act." chip="always live" dark />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {RED_FLAGS.map((f, i) => {
            const on = open === i;
            return (
              <motion.button
                key={f.tag}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 6) * 0.05 }}
                onClick={() => setOpen(on ? null : i)}
                className="text-left rounded-2xl px-4 py-3.5 transition-colors"
                style={{ background: on ? "rgba(255,255,255,0.13)" : "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                data-testid={`flag-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className="flex gap-[3px] shrink-0">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className={d < f.level ? "pulse-soft" : ""} style={{ width: 4, height: 14, borderRadius: 2, background: d < f.level ? (f.level === 3 ? "#FF4D3D" : "#FFA23D") : "rgba(255,255,255,0.15)", animationDelay: `${d * 0.25}s` }} />
                    ))}
                  </span>
                  <span className="text-[8px] tracking-[0.14em] uppercase w-[80px] shrink-0 whitespace-nowrap" style={{ color: "rgba(255,255,255,0.4)" }}>{f.sys}</span>
                  <span className="text-sm font-semibold text-white flex-1">{f.tag}</span>
                </div>
                <AnimatePresence initial={false}>
                  {on && (
                    <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden text-sm text-white/60 pl-[104px] sm:pl-[124px] pt-2">
                      {f.detail}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </div>
        <p className="text-[11px] text-white/35 mt-6">Emergency services, 112. Every one of these outranks any read on this page.</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- 02 panels */
function Bar({ m }) {
  const [open, setOpen] = useState(false);
  const span = m.max - m.min;
  const left = ((m.lo - m.min) / span) * 100;
  const width = ((m.hi - m.lo) / span) * 100;
  return (
    <div className="py-3.5 border-b" style={{ borderColor: "rgba(58,24,16,0.1)" }} data-testid={`marker-${m.name}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-display text-lg" style={{ color: "#3A1810" }}>{m.name}</span>
          <span className="text-[11px] font-semibold" style={{ color: DEEP }}>{m.lo === 0 ? `under ${m.hi}` : `${m.lo} – ${m.hi}`} {m.unit}</span>
        </div>
        <div className="mt-2.5 h-[26px] relative rounded-lg overflow-hidden" style={{ background: "rgba(58,24,16,0.06)" }}>
          <motion.div
            className="absolute top-0 bottom-0"
            style={{ left: `${left}%`, background: "linear-gradient(90deg, rgba(43,125,84,0.35), rgba(43,125,84,0.55))" }}
            initial={{ width: 0 }} whileInView={{ width: `${width}%` }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
          <span className="absolute inset-y-0 left-2 flex items-center text-[9px] tracking-[0.14em] uppercase" style={{ color: "rgba(58,24,16,0.4)" }}>low</span>
          <span className="absolute inset-y-0 right-2 flex items-center text-[9px] tracking-[0.14em] uppercase" style={{ color: "rgba(58,24,16,0.4)" }}>high</span>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden text-sm pt-2.5" style={{ color: "#8A6157" }}>
            {m.means}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

function Panels() {
  const [k, setK] = useState(PANELS[0].key);
  const p = PANELS.find((x) => x.key === k);
  return (
    <section className="relative" data-testid="deck-panels">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <Head num="02 · the sheet" title="Read your bloods" line="Every line drawn as a range. Tap a marker to hear what it actually measures." chip="your own values arriving" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {PANELS.map((x) => (
                <button
                  key={x.key} onClick={() => setK(x.key)}
                  className="shrink-0 lg:w-full text-left rounded-xl px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap"
                  style={k === x.key ? { background: DEEP, color: "#fff" } : { background: "rgba(255,255,255,0.6)", color: "#5C2C20", border: "1px solid rgba(58,24,16,0.1)" }}
                  data-testid={`panel-tab-${x.key}`}
                >
                  {x.name}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div key={k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
                className="rounded-[1.6rem] p-6 sm:p-8"
                style={{ background: "linear-gradient(180deg, #FFFBF8, #FDF2EC)", border: "1px solid rgba(58,24,16,0.12)", boxShadow: "0 24px 60px -30px rgba(90,30,18,0.4)" }}
              >
                <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#A67C70" }}>{p.name}</p>
                <p className="font-display text-xl mt-1" style={{ color: "#3A1810" }}>{p.why}</p>
                <div className="mt-4">{p.markers.map((m) => <Bar key={m.name} m={m} />)}</div>
                <div className="mt-5 flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "rgba(226,109,90,0.08)" }}>
                  <Lock size={13} style={{ color: DEEP }} />
                  <input disabled placeholder="Enter your own values and see them placed on the bars" className="flex-1 bg-transparent text-sm outline-none" style={{ color: "#5C2C20" }} data-testid="panel-value-input" />
                  <span className="text-[9px] tracking-[0.16em] uppercase hidden sm:block" style={{ color: "#A67C70" }}>next release</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ 03 library */
function Library() {
  const [q, setQ] = useState("");
  const [sys, setSys] = useState("All");
  const [open, setOpen] = useState(null);
  const systems = useMemo(() => ["All", ...Array.from(new Set(CONDITIONS.map((c) => c.system)))], []);
  const list = CONDITIONS.filter((c) => (sys === "All" || c.system === sys) && c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #F6E7DF, #FAF0EA)" }} data-testid="deck-library">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <Head num="03 · the wall" title="Conditions" line="Twenty four, colour coded by system. One tap opens the whole thing." chip="sixty more being written" />
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="flex items-center gap-2 rounded-full px-4 py-2.5 flex-1 min-w-[200px]" style={{ background: "#fff", border: "1px solid rgba(58,24,16,0.12)" }}>
            <Search size={13} style={{ color: DEEP }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-transparent text-sm outline-none flex-1" data-testid="library-search" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto">
            {systems.slice(0, 7).map((s) => (
              <button key={s} onClick={() => setSys(s)} className="shrink-0 rounded-full px-3 py-2 text-[11px] font-semibold transition-colors"
                style={sys === s ? { background: "#3A1810", color: "#fff" } : { background: "rgba(255,255,255,0.7)", color: "#5C2C20" }}
                data-testid={`library-sys-${s}`}
              >{s}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {list.map((c, i) => {
            const hue = SYSTEM_HUE[c.system] || HUE;
            return (
              <motion.button
                key={c.name}
                initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-30px" }} transition={{ delay: (i % 8) * 0.03 }}
                onClick={() => setOpen(c)}
                className="group relative text-left rounded-2xl p-4 h-[112px] overflow-hidden transition-transform hover:-translate-y-1"
                style={{ background: "#fff", border: "1px solid rgba(58,24,16,0.1)" }}
                data-testid={`condition-${c.name}`}
              >
                <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: hue }} />
                <span className="absolute -right-6 -bottom-6 w-16 h-16 rounded-full opacity-10 group-hover:opacity-25 transition-opacity" style={{ background: hue }} />
                <p className="text-[9px] tracking-[0.14em] uppercase" style={{ color: hue }}>{c.system}</p>
                <p className="font-display text-lg leading-tight mt-1.5" style={{ color: "#3A1810" }}>{c.name}</p>
                <ArrowRight size={13} className="absolute right-3 bottom-3 opacity-25 group-hover:opacity-70 transition-opacity" style={{ color: hue }} />
              </motion.button>
            );
          })}
        </div>
        {list.length === 0 && <p className="text-sm" style={{ color: "#8A6157" }}>Nothing under that yet.</p>}
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)} className="fixed inset-0 z-[85]" style={{ background: "rgba(30,8,4,0.5)", backdropFilter: "blur(6px)" }} />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 240 }}
              className="fixed right-0 top-0 bottom-0 z-[86] w-full sm:w-[440px] overflow-y-auto p-7"
              style={{ background: "linear-gradient(180deg, #FFFBF8, #FBEDE6)" }}
              data-testid="condition-drawer"
            >
              <button onClick={() => setOpen(null)} className="absolute right-5 top-5 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(58,24,16,0.07)" }} data-testid="drawer-close"><X size={15} style={{ color: "#3A1810" }} /></button>
              <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: SYSTEM_HUE[open.system] || HUE }}>{open.system}</p>
              <h3 className="font-display text-3xl mt-2" style={{ color: "#3A1810" }}>{open.name}</h3>
              {[["From the inside", open.inside], ["What makes it worse", open.worse], ["What a doctor does", open.doctor]].map(([t, v]) => (
                <div key={t} className="mt-6">
                  <p className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#A67C70" }}>{t}</p>
                  <p className="text-[15px] leading-relaxed mt-1.5" style={{ color: "#4A2318" }}>{v}</p>
                </div>
              ))}
              <p className="text-[11px] mt-8" style={{ color: "#A67C70" }}>The full article, with the evidence and the arguments, is being written.</p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}

/* --------------------------------------------------------------- 04 logs */
function Logs({ logs, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ region: "", note: "", date: new Date().toISOString().slice(0, 10) });
  const TONE = { emergency: "#B3261E", urgent: "#C4571F", soon: "#B08019", watch: "#2B7D54", logged: "#8A6157" };
  return (
    <section data-testid="deck-logs">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <Head num="04 · the record" title="Your log" line="Every read you keep lands here, in order. The only record that follows you between doctors." chip={`${logs.length} kept`} />
        <div className="rounded-[1.6rem] p-6 sm:p-8" style={{ background: "linear-gradient(150deg, #3A1810, #5C2418)" }}>
          {logs.length === 0 ? (
            <p className="text-sm text-white/55">Nothing yet. Run a read on the body above and press keep.</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-3">
              {logs.map((l) => (
                <motion.div key={l.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="group shrink-0 w-[230px] rounded-2xl p-4 relative"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}
                  data-testid={`log-item-${l.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full block" style={{ background: TONE[l.band] || TONE.logged }} />
                  <p className="text-sm font-semibold text-white mt-2.5">{l.region}</p>
                  <p className="text-[11px] text-white/45 mt-0.5">{l.date}{l.window ? ` · ${l.window}` : ""}</p>
                  {l.note && <p className="text-[13px] text-white/65 mt-2">{l.note}</p>}
                  <button onClick={() => onRemove(l.id)} className="absolute right-3 top-3 opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity text-white" data-testid={`log-remove-${l.id}`}><Trash2 size={12} /></button>
                </motion.div>
              ))}
            </div>
          )}
          <button onClick={() => setOpen(!open)} className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold" style={{ background: "#fff", color: "#7A1E12" }} data-testid="log-add-toggle">
            <Plus size={12} /> Add by hand
          </button>
          <AnimatePresence>
            {open && (
              <motion.form
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                onSubmit={(e) => { e.preventDefault(); if (!form.region.trim()) return; onAdd({ ...form, id: `m${Date.now()}`, band: "logged" }); setForm({ region: "", note: "", date: new Date().toISOString().slice(0, 10) }); setOpen(false); }}
                className="overflow-hidden"
              >
                <div className="mt-4 flex flex-wrap gap-2">
                  <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" className="rounded-xl px-3 py-2 text-sm" style={{ background: "rgba(255,255,255,0.9)" }} data-testid="log-date" />
                  <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="What part of you" className="flex-1 min-w-[160px] rounded-xl px-3 py-2 text-sm" style={{ background: "rgba(255,255,255,0.9)" }} data-testid="log-region" />
                  <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What happened" className="flex-1 min-w-[200px] rounded-xl px-3 py-2 text-sm" style={{ background: "rgba(255,255,255,0.9)" }} data-testid="log-note" />
                  <button type="submit" className="rounded-full px-4 py-2 text-xs font-semibold text-white" style={{ background: HUE }} data-testid="log-submit">Keep</button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ 05 builder */
function Builder() {
  const [picked, setPicked] = useState(["cause", "flags"]);
  const questions = APPOINTMENT_TOPICS.filter((t) => picked.includes(t.key)).flatMap((t) => t.questions);
  return (
    <section style={{ background: "linear-gradient(180deg, #FAF0EA, #F6E7DF)" }} data-testid="deck-builder">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <Head num="05 · the sheet you take in" title="Ten minutes, used well" line="Pick what you need out of the appointment. The sentences write themselves." chip="printing arriving" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 flex flex-wrap gap-2 content-start">
            {APPOINTMENT_TOPICS.map((t) => (
              <button key={t.key} onClick={() => setPicked((p) => (p.includes(t.key) ? p.filter((x) => x !== t.key) : [...p, t.key]))}
                className="rounded-full px-4 py-2.5 text-sm font-medium transition-all"
                style={picked.includes(t.key) ? { background: HUE, color: "#fff", boxShadow: "0 10px 26px -12px rgba(226,109,90,0.9)" } : { background: "#fff", color: "#5C2C20", border: "1px solid rgba(58,24,16,0.12)" }}
                data-testid={`topic-${t.key}`}
              >{t.label}</button>
            ))}
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-[1.4rem] p-6 relative overflow-hidden" style={{ background: "repeating-linear-gradient(180deg, #FFFCFA, #FFFCFA 31px, rgba(58,24,16,0.07) 32px)", border: "1px solid rgba(58,24,16,0.12)", boxShadow: "0 20px 50px -28px rgba(90,30,18,0.4)" }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#A67C70" }}>{questions.length} questions</p>
                <button onClick={() => { navigator.clipboard.writeText(questions.map((q) => `- ${q}`).join("\n")); toast("Copied. Take it in with you."); }} disabled={!questions.length} className="text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" style={{ color: DEEP }} data-testid="builder-copy"><Copy size={11} /> copy</button>
              </div>
              {questions.length === 0 ? (
                <p className="text-sm" style={{ color: "#8A6157" }}>Pick a topic and the questions appear here.</p>
              ) : (
                <ol className="space-y-[10px]" data-testid="builder-list">
                  {questions.map((q, i) => (
                    <motion.li key={q} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }} className="text-[15px] flex gap-3 leading-8" style={{ color: "#3A1810" }}>
                      <span className="text-[11px] font-mono-nc pt-2" style={{ color: "#C08878" }}>{String(i + 1).padStart(2, "0")}</span>{q}
                    </motion.li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SecondaryDeck({ logs, onAdd, onRemove }) {
  return (
    <div style={{ background: "#FAF0EA" }}>
      <Flags />
      <Panels />
      <Library />
      <Logs logs={logs} onAdd={onAdd} onRemove={onRemove} />
      <Builder />
    </div>
  );
}
